import React, { useState, useEffect } from 'react';
import { Shield, ShieldCheck, ShieldAlert, ChevronDown, Cpu, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const scanMessages = [
  '🔍 Vigilando eventos del kernel (FIM)...',
  '✓ Integridad index.html validada.',
  '✓ Claves asimétricas RSA-2048 OK.',
  '🔍 Escaneando inyecciones de código...',
  '✓ SQLite local saludable.',
  '✓ 0 amenazas en caliente.',
  '🔍 Escaneando scripts PHP/JS...',
  '✓ Self-Healing activo (100% íntegro).'
];

export default function SecurityMonitorWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [localTime, setLocalTime] = useState('');

  useEffect(() => {
    const handleShow = () => {
      setIsDismissed(false);
      setIsOpen(true);
    };
    window.addEventListener('show-secops-monitor', handleShow);
    return () => window.removeEventListener('show-secops-monitor', handleShow);
  }, []);
  const [scanMessage, setScanMessage] = useState('Vigilando eventos del kernel (FIM)...');
  const [scanIndex, setScanIndex] = useState(0);
  
  // Estado de seguridad: 
  // 'healthy' (verde - saludable)
  // 'under-attack-warning' (naranja - algo raro detectado)
  // 'under-attack-critical' (rojo - ataque malintencionado confirmado)
  // 'healed' (verde esmeralda - restaurado con éxito)
  const [securityState, setSecurityState] = useState('healthy');
  const [attackDetails, setAttackDetails] = useState({ type: '', filename: '', isReal: false });

  // Actualizar hora local cada segundo
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setLocalTime(now.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // Rotar mensajes del escáner cada 3 segundos (solo si el estado es saludable)
  useEffect(() => {
    if (securityState !== 'healthy') return;

    const interval = setInterval(() => {
      setScanIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % scanMessages.length;
        setScanMessage(scanMessages[nextIndex]);
        return nextIndex;
      });
    }, 3000);
    return () => clearInterval(interval);
  }, [securityState]);

  // Escuchar eventos globales de ciberseguridad
  useEffect(() => {
    const handleAttack = (e) => {
      const { type, filename, isReal, severity } = e.detail;
      setAttackDetails({ type, filename, isReal });
      
      if (severity === 'warning') {
        setSecurityState('under-attack-warning');
        setScanMessage(`[!] ANOMALÍA: ¡Evento inusual detectado en ${filename}! Analizando integridad de archivos y firmas...`);
      } else {
        setSecurityState('under-attack-critical');
        setScanMessage(`[!] ALERTA CRÍTICA: ¡Intrusión malintencionada detectada en ${filename}! Desplegando parche RSA-2048...`);
      }
      // Forzar que el panel se abra de inmediato ante cualquier anomalía
      setIsOpen(true);
    };

    const handleHealed = (e) => {
      const { type, filename, isReal } = e.detail;
      setSecurityState('healed');
      setScanMessage(`✓ Self-Healing completado. Repuesto código original de ${filename} firmado digitalmente.`);
      
      // Volver a estado saludable normal después de 6 segundos
      const timeout = setTimeout(() => {
        setSecurityState('healthy');
        setAttackDetails({ type: '', filename: '', isReal: false });
        setScanMessage('✓ Autoconservación en caliente completada. Estado: 100% Íntegro.');
      }, 6000);

      return () => clearTimeout(timeout);
    };

    window.addEventListener('security-attack', handleAttack);
    window.addEventListener('security-healed', handleHealed);

    return () => {
      window.removeEventListener('security-attack', handleAttack);
      window.removeEventListener('security-healed', handleHealed);
    };
  }, []);

  if (isDismissed) return null;

  return (
    <div className="fixed top-24 right-4 z-40 max-w-[240px] sm:max-w-[260px] w-full font-sans transition-all duration-300 ease-in-out">
      {isOpen ? (
        /* Widget Abierto (Llamativo y adaptado a estados) */
        <div className={`bg-[#0b0e22]/95 backdrop-blur-lg border p-3.5 rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.6)] text-white relative transition-all duration-300 ${
          securityState === 'under-attack-critical' 
            ? 'border-red-500/80 shadow-[0_0_25px_rgba(239,68,68,0.5)]' 
            : securityState === 'under-attack-warning'
            ? 'border-amber-500/70 shadow-[0_0_20px_rgba(245,158,11,0.4)]'
            : securityState === 'healed'
            ? 'border-emerald-500/60 shadow-[0_0_20px_rgba(16,185,129,0.3)]'
            : 'border-amber-500/30'
        }`}>
          
          {/* Cabecera compacta */}
          <div className="flex justify-between items-center border-b border-gray-800 pb-2 mb-2">
            <div className="flex items-center gap-1">
              <span className="relative flex h-1.5 w-1.5">
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                  securityState === 'under-attack-critical' 
                    ? 'bg-red-400' 
                    : securityState === 'under-attack-warning'
                    ? 'bg-amber-400'
                    : 'bg-emerald-400'
                }`}></span>
                <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${
                  securityState === 'under-attack-critical' 
                    ? 'bg-red-500' 
                    : securityState === 'under-attack-warning'
                    ? 'bg-amber-500'
                    : 'bg-emerald-500'
                }`}></span>
              </span>
              <h4 className={`text-[9px] font-black uppercase tracking-widest ${
                securityState === 'under-attack-critical' 
                  ? 'text-red-500 animate-pulse' 
                  : securityState === 'under-attack-warning'
                  ? 'text-amber-500 animate-pulse'
                  : securityState === 'healed'
                  ? 'text-emerald-400 animate-pulse'
                  : 'text-amber-400'
              }`}>SecOps</h4>
            </div>
            
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] font-mono text-gray-400 bg-gray-900 px-1.5 py-0.2 rounded">
                {localTime}
              </span>
              <button 
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Minimizar panel"
                title="Minimizar panel"
              >
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              <button 
                onClick={() => setIsDismissed(true)}
                className="text-red-400 hover:text-red-300 transition-colors ml-1 font-bold text-xs flex items-center justify-center"
                aria-label="Ocultar monitor SecOps de pantalla"
                title="Ocultar de pantalla"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* Estado de Saneamiento según severidad */}
          {securityState === 'under-attack-critical' ? (
            <div className="flex items-center gap-2 bg-red-500/20 border border-red-500/30 p-2 rounded-lg mb-2 animate-pulse">
              <ShieldAlert className="w-6 h-6 text-red-500 flex-shrink-0" />
              <div>
                <p className="text-[7.5px] text-red-400/90 font-black uppercase tracking-wider leading-none">🚨 INTRUSIÓN MALICIOSA</p>
                <h5 className="text-[9px] font-black text-white mt-0.5 leading-none">MITIGANDO EN TIEMPO REAL</h5>
              </div>
            </div>
          ) : securityState === 'under-attack-warning' ? (
            <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 p-2 rounded-lg mb-2 animate-pulse">
              <ShieldAlert className="w-6 h-6 text-amber-500 flex-shrink-0" />
              <div>
                <p className="text-[7.5px] text-amber-400/90 font-black uppercase tracking-wider leading-none">⚠️ ANOMALÍA DETECTADA</p>
                <h5 className="text-[9px] font-black text-white mt-0.5 leading-none">ANALIZANDO INTEGRIDAD</h5>
              </div>
            </div>
          ) : securityState === 'healed' ? (
            <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 p-2 rounded-lg mb-2">
              <ShieldCheck className="w-6 h-6 text-emerald-400 flex-shrink-0 animate-bounce" />
              <div>
                <p className="text-[7.5px] text-emerald-400/90 font-black uppercase tracking-wider leading-none">AUTOSANACIÓN COMPLETADA</p>
                <h5 className="text-[9px] font-black text-white mt-0.5 leading-none">INTEGRIDAD RESTAURADA</h5>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 p-2 rounded-lg mb-2">
              <ShieldCheck className="w-6 h-6 text-emerald-400 flex-shrink-0" />
              <div>
                <p className="text-[8px] text-emerald-400/80 font-bold uppercase tracking-wider leading-none">SANEAMIENTO WEB</p>
                <h5 className="text-[10px] font-black text-white mt-0.5 leading-none">ACTIVO Y PROTEGIDO</h5>
              </div>
            </div>
          )}

          {/* Consola de Escaneo en Caliente */}
          <div className={`bg-[#050715] border px-2 py-1.5 rounded-lg mb-2 font-mono text-[8px] min-h-[30px] flex items-center transition-colors duration-300 ${
            securityState === 'under-attack-critical' 
              ? 'border-red-800 text-red-400 font-bold' 
              : securityState === 'under-attack-warning'
              ? 'border-amber-700 text-amber-400 font-bold'
              : securityState === 'healed'
              ? 'border-emerald-800 text-emerald-400 font-bold'
              : 'border-gray-800 text-emerald-400'
          }`}>
            <span className="leading-tight">{scanMessage}</span>
          </div>

          {/* Información de Ciberseguridad */}
          <div className="text-[9px] text-gray-400 leading-normal mb-2 border-t border-gray-800/60 pt-2">
            <p>
              <strong className="text-gray-200">Autoreparación:</strong> Blindada por firma asimétrica **RSA-2048** y SOC con **IA**.
            </p>
          </div>

          {/* Botón de Acción Compacto */}
          <Link
            to="/self-healing"
            className={`flex items-center justify-center gap-1 w-full py-1.5 text-[9px] font-black uppercase tracking-wider rounded-lg shadow-md transition-all duration-200 hover:scale-[1.02] ${
              securityState === 'under-attack-critical'
                ? 'bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800'
                : securityState === 'under-attack-warning'
                ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 hover:from-amber-600 hover:to-amber-700'
                : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-950'
            }`}
          >
            <Cpu className="w-3 h-3 animate-spin" style={{ animationDuration: securityState === 'under-attack-critical' ? '0.8s' : securityState === 'under-attack-warning' ? '1.5s' : '3s' }} /> Consola SOC
          </Link>

        </div>
      ) : (
        /* Widget Colapsado (Botón Flotante con animaciones y colores) */
        <div className="relative group/secops ml-auto w-fit">
          {/* Botón sutil de ocultar, visible al hacer hover */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsDismissed(true);
            }}
            className="absolute -top-1 -right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-0.5 w-4 h-4 text-[9px] flex items-center justify-center border border-white/20 shadow-md opacity-0 group-hover/secops:opacity-100 transition-opacity duration-200 z-[10000]"
            title="Ocultar monitor SecOps"
            aria-label="Ocultar de pantalla"
          >
            <X className="w-2.5 h-2.5" />
          </button>
          <button
            onClick={() => setIsOpen(true)}
            className={`flex items-center justify-center w-10 h-10 bg-[#0b0e22] border rounded-full shadow-lg transition-all duration-300 hover:scale-110 relative ${
              securityState === 'under-attack-critical' 
                ? 'border-red-500 text-red-500 animate-bounce' 
                : securityState === 'under-attack-warning'
                ? 'border-amber-500 text-amber-500 animate-pulse'
                : securityState === 'healed'
                ? 'border-emerald-500 text-emerald-500'
                : 'border-amber-500/50 hover:border-amber-400 text-amber-400 hover:text-amber-300'
            }`}
            title="Ver monitor de ciberseguridad autónoma"
          >
          {securityState === 'under-attack-critical' ? (
            <ShieldAlert className="w-5 h-5 animate-pulse text-red-500" />
          ) : securityState === 'under-attack-warning' ? (
            <ShieldAlert className="w-5 h-5 animate-pulse text-amber-500" />
          ) : (
            <Shield className="w-5 h-5 animate-pulse" />
          )}
          
          <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
              securityState === 'under-attack-critical' 
                ? 'bg-red-400' 
                : securityState === 'under-attack-warning'
                ? 'bg-amber-400'
                : 'bg-emerald-400'
            }`}></span>
            <span className={`relative inline-flex rounded-full h-2 w-2 ${
              securityState === 'under-attack-critical' 
                ? 'bg-red-500' 
                : securityState === 'under-attack-warning'
                ? 'bg-amber-500'
                : 'bg-emerald-500'
            }`}></span>
          </span>
          {/* Tooltip */}
          <span className="absolute right-12 bg-[#0b0e22] border border-gray-800 text-white text-[8px] font-bold py-1 px-2 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap shadow-md pointer-events-none">
            {securityState === 'under-attack-critical' 
              ? 'SecOps: AMENAZA CRÍTICA' 
              : securityState === 'under-attack-warning'
              ? 'SecOps: ALGO EXTRAÑO DETECTADO'
              : 'SecOps: Activo'}
          </span>
        </button>
        </div>
      )}
    </div>
  );
}
